import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, LayoutDashboard, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Navbar as RBNavbar,
  Container,
  Button,
  Offcanvas,
  Nav,
} from "react-bootstrap";
import { NAVIGATION, COLORS, SITE_CONFIG } from "../../../Utiles/Constants";
import { useDispatch } from "react-redux";
import useUser from "../../../hooks/useUser";
import { logout } from "../../../redux/features/userSlice";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, isAdmin, isCustomer } = useUser();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href) => {
    if (href.startsWith("/#")) {
      return location.pathname === "/" && location.hash === href.slice(1);
    }
    return location.pathname === href;
  };

  const handleNavClick = () => {
    setExpanded(false);
  };
  const handleScrollTo = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }

    setExpanded(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
    setExpanded(false);
  };

  const handleDashboard = () => {
    if (isAdmin) {
      window.open("/admin/dashboard", "_blank");
    } else if (isCustomer) {
      window.open("/admin/dashboard", "_blank");
    }
    setExpanded(false);
  };
  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-100 position-fixed top-0 start-0 end-0"
      style={{ zIndex: 1030 }}
    >
      <RBNavbar
        className="transition-all duration-300"
        style={{
          backgroundColor: !isScrolled
            ? COLORS.background
            : "rgba(255, 255, 255, 0.1)",
          backdropFilter: !isScrolled ? "none" : "blur(12px)",
          WebkitBackdropFilter: !isScrolled ? "none" : "blur(12px)",
          boxShadow: !isScrolled
            ? "0 2px 8px rgba(0, 0, 0, 0.25)"
            : "0 4px 30px rgba(0, 0, 0, 0.1)",
          borderBottom: `1px solid ${COLORS.background}`,
          transition: "all 0.3s ease",
        }}
      >
        <Container>
          {/* Brand */}
          <RBNavbar.Brand
            as={Link}
            to="/"
            className="d-flex align-items-center text-decoration-none"
            onClick={handleNavClick}
          >
            <motion.div
              animate={{ rotate: isScrolled ? 360 : 0 }}
              transition={{ duration: 0.5 }}
              className="me-2 p-1 rounded-circle"
            >
              <MessageCircle size={28} style={{ color: COLORS.primary }} />
            </motion.div>
            <span
              className="fw-bold fs-4"
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {SITE_CONFIG.name}
            </span>
          </RBNavbar.Brand>

          {/* Desktop Nav (visible on lg and above) */}
          <Nav className="ms-auto d-none d-lg-flex align-items-center gap-3">
            {NAVIGATION.map((item) => {
              const active = isActive(item.href);
              return (
                <Nav.Link
                  key={item.name}
                  onClick={() => handleScrollTo(item.href.replace("#", ""))}
                  className={`fw-medium px-3 py-2 rounded-pill ${
                    active ? "text-white" : "text-white"
                  }`}
                  style={{
                    backgroundColor: active ? COLORS.primary : "transparent",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                >
                  {item.name}
                </Nav.Link>
              );
            })}

            {token ? (
              <>
                <Button
                  onClick={handleDashboard}
                  className="fw-semibold px-4 py-2 rounded-pill border-0 text-white d-flex align-items-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  }}
                >
                  <LayoutDashboard size={18} />
                  Dashboard
                </Button>
                <Button
                  onClick={handleLogout}
                  className="fw-medium px-4 py-2 rounded-pill"
                  variant="outline-secondary"
                  style={{
                    borderColor: COLORS.error,
                    color: COLORS.error,
                  }}
                >
                  <LogOut size={18} />
                </Button>
              </>
            ) : (
              <>
                <Nav.Link
                  as={Link}
                  to="/login"
                  onClick={handleNavClick}
                  className="fw-medium px-3 py-2 rounded-pill"
                  style={{ color: COLORS.secondary }}
                >
                  Login
                </Nav.Link>
                <Button
                  as={Link}
                  to="/register"
                  onClick={handleNavClick}
                  className="fw-semibold px-4 py-2 rounded-pill border-0 text-white"
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Nav>

          {/* Mobile Toggle (only visible < lg) */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="border-0 p-2 rounded-3 d-lg-none"
            onClick={() => setExpanded(true)}
            style={{
              backgroundColor: COLORS.primary + "15",
              transition: "all 0.3s ease",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key="menu"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu size={24} style={{ color: COLORS.primary }} />
              </motion.div>
            </AnimatePresence>
          </motion.button>
        </Container>
      </RBNavbar>
      <Offcanvas
        show={expanded}
        onHide={handleNavClick}
        placement="end"
        className="d-lg-none"
        style={{
          backgroundColor: COLORS.background + "f2",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
        }}
      >
        <Offcanvas.Header
          closeButton={false}
          className="border-bottom pb-3"
          style={{
            borderColor: COLORS.primary + "20",
          }}
        >
          <Offcanvas.Title className="d-flex align-items-center w-100 justify-content-between">
            <div className="d-flex align-items-center">
              <MessageCircle
                size={24}
                style={{ color: COLORS.primary }}
                className="me-2"
              />
              <span
                className="fw-bold fs-5"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {SITE_CONFIG.name}
              </span>
            </div>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              className="btn btn-sm rounded-circle border-0 p-2"
              onClick={handleNavClick}
              style={{
                backgroundColor: COLORS.error + "15",
                color: COLORS.error,
              }}
            >
              <X size={20} />
            </motion.button>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-4">
          {/* Mobile Navigation Links */}
          <div className="d-flex flex-column gap-2 mb-4">
            {NAVIGATION.map((item, index) => {
              const active = isActive(item.href);
              return (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    key={item.name}
                    onClick={() => handleScrollTo(item.href.replace("#", ""))}
                    className="text-decoration-none d-block p-3 rounded-3 fw-medium"
                    style={{
                      backgroundColor: active ? COLORS.primary : "transparent",
                      color: "white",
                      cursor: "pointer",
                    }}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div
            className="d-flex flex-column gap-3 pt-3 border-top"
            style={{ borderColor: COLORS.primary + "20" }}
          >
            {token ? (
              <>
                <Button
                  onClick={handleDashboard}
                  className="btn w-100 py-3 fw-semibold rounded-3 text-white d-flex align-items-center justify-content-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                    border: "none",
                  }}
                >
                  <LayoutDashboard size={20} />
                  Open Dashboard
                </Button>
                <Button
                  onClick={handleLogout}
                  className="btn btn-outline w-100 py-3 fw-medium rounded-3"
                  style={{ borderColor: COLORS.error, color: COLORS.error }}
                >
                  <LogOut size={18} className="me-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={handleNavClick}
                  className="btn btn-outline w-100 py-3 fw-medium rounded-3"
                  style={{
                    borderColor: COLORS.secondary,
                    color: COLORS.secondary,
                  }}
                >
                  Login to Account
                </Link>
                <Link
                  to="/register"
                  onClick={handleNavClick}
                  className="btn w-100 py-3 fw-semibold rounded-3 text-white"
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                    border: "none",
                  }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </motion.div>
  );
};

export default Navbar;
